import React, { useState, useMemo } from "react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { Plus, X, AlertTriangle, Globe, Phone, MessageSquare, Copy, ChevronDown } from "lucide-react";
import { AppButton } from "../ui";
import "./create-template-form.scss";

const CATEGORIES = [
  { value: "UTILITY", label: "Utility", desc: "Send account updates, order updates, alerts" },
  { value: "MARKETING", label: "Marketing", desc: "Send promotions, offers, recommendations" },
  { value: "AUTHENTICATION", label: "Authentication", desc: "Send OTPs for identity verification" },
];

const LANGUAGES = [
  { value: "af", label: "Afrikaans" },
  { value: "sq", label: "Albanian" },
  { value: "ar", label: "Arabic" },
  { value: "ar_EG", label: "Arabic (EGY)" },
  { value: "ar_AE", label: "Arabic (UAE)" },
  { value: "ar_LB", label: "Arabic (LBN)" },
  { value: "ar_MA", label: "Arabic (MAR)" },
  { value: "ar_QA", label: "Arabic (QAT)" },
  { value: "az", label: "Azerbaijani" },
  { value: "be_BY", label: "Belarusian" },
  { value: "bn", label: "Bengali" },
  { value: "bn_IN", label: "Bengali (IND)" },
  { value: "bg", label: "Bulgarian" },
  { value: "ca", label: "Catalan" },
  { value: "zh_CN", label: "Chinese (CHN)" },
  { value: "zh_HK", label: "Chinese (HKG)" },
  { value: "zh_TW", label: "Chinese (TAI)" },
  { value: "hr", label: "Croatian" },
  { value: "cs", label: "Czech" },
  { value: "da", label: "Danish" },
  { value: "prs_AF", label: "Dari" },
  { value: "nl", label: "Dutch" },
  { value: "nl_BE", label: "Dutch (BEL)" },
  { value: "en", label: "English" },
  { value: "en_GB", label: "English (UK)" },
  { value: "en_US", label: "English (US)" },
  { value: "en_AE", label: "English (UAE)" },
  { value: "en_AU", label: "English (AUS)" },
  { value: "en_CA", label: "English (CAN)" },
  { value: "en_GH", label: "English (GHA)" },
  { value: "en_IE", label: "English (IRL)" },
  { value: "en_IN", label: "English (IND)" },
  { value: "en_JM", label: "English (JAM)" },
  { value: "en_MY", label: "English (MYS)" },
  { value: "en_NZ", label: "English (NZL)" },
  { value: "en_QA", label: "English (QAT)" },
  { value: "en_SG", label: "English (SGP)" },
  { value: "en_UG", label: "English (UGA)" },
  { value: "en_ZA", label: "English (ZAF)" },
  { value: "et", label: "Estonian" },
  { value: "fil", label: "Filipino" },
  { value: "fi", label: "Finnish" },
  { value: "fr", label: "French" },
  { value: "fr_BE", label: "French (BEL)" },
  { value: "fr_CA", label: "French (CAN)" },
  { value: "fr_CH", label: "French (CHE)" },
  { value: "fr_CI", label: "French (CIV)" },
  { value: "fr_MA", label: "French (MAR)" },
  { value: "ka", label: "Georgian" },
  { value: "de", label: "German" },
  { value: "de_AT", label: "German (AUT)" },
  { value: "de_CH", label: "German (CHE)" },
  { value: "el", label: "Greek" },
  { value: "gu", label: "Gujarati" },
  { value: "ha", label: "Hausa" },
  { value: "he", label: "Hebrew" },
  { value: "hi", label: "Hindi" },
  { value: "hu", label: "Hungarian" },
  { value: "id", label: "Indonesian" },
  { value: "ga", label: "Irish" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "kn", label: "Kannada" },
  { value: "kk", label: "Kazakh" },
  { value: "rw_RW", label: "Kinyarwanda" },
  { value: "ko", label: "Korean" },
  { value: "ky_KG", label: "Kyrgyz" },
  { value: "lo", label: "Lao" },
  { value: "lv", label: "Latvian" },
  { value: "lt", label: "Lithuanian" },
  { value: "mk", label: "Macedonian" },
  { value: "ms", label: "Malay" },
  { value: "ml", label: "Malayalam" },
  { value: "mr", label: "Marathi" },
  { value: "nb", label: "Norwegian" },
  { value: "ps_AF", label: "Pashto" },
  { value: "fa", label: "Persian" },
  { value: "pl", label: "Polish" },
  { value: "pt_BR", label: "Portuguese (BR)" },
  { value: "pt_PT", label: "Portuguese (POR)" },
  { value: "pa", label: "Punjabi" },
  { value: "ro", label: "Romanian" },
  { value: "ru", label: "Russian" },
  { value: "sr", label: "Serbian" },
  { value: "si_LK", label: "Sinhala" },
  { value: "sk", label: "Slovak" },
  { value: "sl", label: "Slovenian" },
  { value: "es", label: "Spanish" },
  { value: "es_AR", label: "Spanish (ARG)" },
  { value: "es_CL", label: "Spanish (CHL)" },
  { value: "es_CO", label: "Spanish (COL)" },
  { value: "es_CR", label: "Spanish (CRI)" },
  { value: "es_DO", label: "Spanish (DOM)" },
  { value: "es_EC", label: "Spanish (ECU)" },
  { value: "es_HN", label: "Spanish (HND)" },
  { value: "es_MX", label: "Spanish (MEX)" },
  { value: "es_PA", label: "Spanish (PAN)" },
  { value: "es_PE", label: "Spanish (PER)" },
  { value: "es_ES", label: "Spanish (SPA)" },
  { value: "es_UY", label: "Spanish (URY)" },
  { value: "sw", label: "Swahili" },
  { value: "sv", label: "Swedish" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
  { value: "th", label: "Thai" },
  { value: "tr", label: "Turkish" },
  { value: "uk", label: "Ukrainian" },
  { value: "ur", label: "Urdu" },
  { value: "uz", label: "Uzbek" },
  { value: "vi", label: "Vietnamese" },
  { value: "zu", label: "Zulu" },
];

const HEADER_TYPES = [
  { value: "none", label: "None" },
  { value: "text", label: "Text" },
];

// Button types matching WhatsApp Manager options
const BUTTON_TYPES = [
  { value: "QUICK_REPLY", label: "Custom", icon: MessageSquare, desc: "Quick reply button" },
  { value: "URL", label: "Visit website", icon: Globe, desc: "Open a URL" },
  { value: "PHONE_NUMBER", label: "Call Phone Number", icon: Phone, desc: "Dial a phone number" },
  { value: "COPY_CODE", label: "Copy offer code", icon: Copy, desc: "Copy a coupon code" },
];

const EMPTY_FORM = {
  name: "",
  language: "en",
  category: "UTILITY",
  headerType: "none",
  headerText: "",
  body: "",
  footer: "",
  buttons: [],
};

// Helper: count positional params in text
const countParams = (text) => (text?.match(/\{\{\d+\}\}/g) || []).length;
const extractParams = (text) => text?.match(/\{\{\d+\}\}/g) || [];

/**
 * Convert a Meta-spec components array (as stored in WhatsAppTemplate /
 * predefined library) back into the editor form state. Returns
 * { form, bodyExamples, headerExamples }.
 */
export const componentsToFormState = (template = {}) => {
  const components = Array.isArray(template.components) ? template.components : [];
  const form = { ...EMPTY_FORM };
  let bodyExamples = [];
  let headerExamples = [];

  if (template.name) form.name = template.name;
  if (template.language) form.language = template.language;
  if (template.category) form.category = String(template.category).toUpperCase();

  for (const c of components) {
    const type = (c.type || "").toUpperCase();
    if (type === "HEADER") {
      const fmt = (c.format || "TEXT").toUpperCase();
      if (fmt === "TEXT") {
        form.headerType = "text";
        form.headerText = c.text || "";
        const cnt = countParams(form.headerText);
        const samples = c.example?.header_text || [];
        headerExamples = Array.from({ length: cnt }, (_, i) => samples[i] || "");
      }
    } else if (type === "BODY") {
      form.body = c.text || "";
      const cnt = countParams(form.body);
      const row = c.example?.body_text?.[0] || [];
      bodyExamples = Array.from({ length: cnt }, (_, i) => row[i] || "");
    } else if (type === "FOOTER") {
      form.footer = c.text || "";
    } else if (type === "BUTTONS") {
      const btns = Array.isArray(c.buttons) ? c.buttons : [];
      form.buttons = btns.map((b) => {
        const bt = (b.type || "").toUpperCase();
        if (bt === "URL") {
          const isDynamic = /\{\{\d+\}\}/.test(b.url || "");
          return { type: "URL", text: b.text || "", url: b.url || "", urlType: isDynamic ? "dynamic" : "static" };
        }
        if (bt === "PHONE_NUMBER") {
          return { type: "PHONE_NUMBER", text: b.text || "", phone_number: b.phone_number || "" };
        }
        if (bt === "COPY_CODE") {
          return { type: "COPY_CODE", text: b.text || "", example: b.example || "" };
        }
        return { type: "QUICK_REPLY", text: b.text || "" };
      });
    }
  }

  return { form, bodyExamples, headerExamples };
};

const CreateTemplateForm = ({ onSubmit, onCancel, initialValues = null, submitLabel = "Submit for Review" }) => {
  const initial = useMemo(
    () => (initialValues ? componentsToFormState(initialValues) : { form: { ...EMPTY_FORM }, bodyExamples: [], headerExamples: [] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [form, setForm] = useState(initial.form);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [bodyExamples, setBodyExamples] = useState(initial.bodyExamples);
  const [headerExamples, setHeaderExamples] = useState(initial.headerExamples);
  const [showBtnMenu, setShowBtnMenu] = useState(false);

  // Derived counts
  const bodyParamCount = useMemo(() => countParams(form.body), [form.body]);
  const headerParamCount = useMemo(() => countParams(form.headerText), [form.headerText]);
  const bodyCharCount = form.body.length;

  const update = (key) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [key]: val }));
    setError("");

    if (key === "body") {
      const cnt = countParams(val);
      setBodyExamples((prev) => {
        const next = [...prev];
        while (next.length < cnt) next.push("");
        return next.slice(0, cnt);
      });
    }
    if (key === "headerText") {
      const cnt = countParams(val);
      setHeaderExamples((prev) => {
        const next = [...prev];
        while (next.length < cnt) next.push("");
        return next.slice(0, cnt);
      });
    }
  };

  // Insert variable into body text at cursor (append)
  const addBodyVariable = () => {
    const nextIdx = bodyParamCount + 1;
    setForm((prev) => ({ ...prev, body: prev.body + `{{${nextIdx}}}` }));
    setBodyExamples((prev) => [...prev, ""]);
  };

  const addHeaderVariable = () => {
    if (headerParamCount >= 1) return; // Header supports max 1 variable
    setForm((prev) => ({ ...prev, headerText: prev.headerText + "{{1}}" }));
    setHeaderExamples([""] );
  };

  // ── Buttons management ────────────────────────────────────────────────────
  const addButton = (type) => {
    if (form.buttons.length >= 10) return;
    const base = { text: "" };
    if (type === "URL") Object.assign(base, { type: "URL", url: "", urlType: "static" });
    else if (type === "PHONE_NUMBER") Object.assign(base, { type: "PHONE_NUMBER", phone_number: "" });
    else if (type === "COPY_CODE") Object.assign(base, { type: "COPY_CODE", example: "" });
    else Object.assign(base, { type: "QUICK_REPLY" });

    setForm((prev) => ({ ...prev, buttons: [...prev.buttons, base] }));
  };

  const updateButton = (idx, field, value) => {
    setForm((prev) => {
      const buttons = [...prev.buttons];
      buttons[idx] = { ...buttons[idx], [field]: value };
      return { ...prev, buttons };
    });
  };

  const removeButton = (idx) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== idx),
    }));
  };

  // ── Build Meta components array ───────────────────────────────────────────
  const buildComponents = () => {
    const components = [];

    // Header
    if (form.headerType === "text" && form.headerText.trim()) {
      const hdr = { type: "HEADER", format: "TEXT", text: form.headerText.trim() };
      if (headerParamCount > 0) {
        hdr.example = {
          header_text: headerExamples.map((v) => v.trim() || "example"),
        };
      }
      components.push(hdr);
    }

    if (!form.body.trim()) return null;

    // Body
    const bodyComp = { type: "BODY", text: form.body.trim() };
    if (bodyParamCount > 0) {
      bodyComp.example = {
        body_text: [bodyExamples.map((v) => v.trim() || "example")],
      };
    }
    components.push(bodyComp);

    // Footer
    if (form.footer.trim()) {
      components.push({ type: "FOOTER", text: form.footer.trim() });
    }

    // Buttons
    if (form.buttons.length > 0) {
      const btns = form.buttons.map((b) => {
        if (b.type === "URL") {
          const btn = { type: "URL", text: b.text, url: b.url };
          if (b.urlType === "dynamic" && b.url) {
            btn.example = [b.url];
          }
          return btn;
        }
        if (b.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone_number };
        if (b.type === "COPY_CODE") return { type: "COPY_CODE", example: b.example || "DISCOUNT10" };
        return { type: "QUICK_REPLY", text: b.text };
      });
      components.push({ type: "BUTTONS", buttons: btns });
    }

    return components;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) { setError("Template name is required"); return; }
    if (!/^[a-z][a-z0-9_]*$/.test(form.name.trim())) {
      setError("Name must be lowercase, start with a letter, only letters/numbers/underscores");
      return;
    }
    if (!form.body.trim()) { setError("Body text is required"); return; }

    // Validate body examples
    if (bodyParamCount > 0) {
      const missing = bodyExamples.slice(0, bodyParamCount).some((v) => !v?.trim());
      if (missing || bodyExamples.length < bodyParamCount) {
        setError("Sample values are required for all body variables");
        return;
      }
    }

    // Validate header examples
    if (headerParamCount > 0) {
      const missing = headerExamples.slice(0, headerParamCount).some((v) => !v?.trim());
      if (missing || headerExamples.length < headerParamCount) {
        setError("Sample values are required for header variables");
        return;
      }
    }

    const components = buildComponents();
    if (!components) { setError("Body text is required"); return; }

    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        language: form.language,
        category: form.category,
        components,
      });
      setForm({ ...EMPTY_FORM });
      setBodyExamples([]);
      setHeaderExamples([]);
    } catch (err) {
      setError(err.message || "Failed to create template");
    }
    setSubmitting(false);
  };

  // ── Preview text with highlights ──────────────────────────────────────────
  const previewBody = useMemo(() => {
    let text = form.body;
    const params = extractParams(text);
    params.forEach((p, i) => {
      const example = bodyExamples[i]?.trim();
      if (example) {
        text = text.replace(p, example);
      }
    });
    return text;
  }, [form.body, bodyExamples]);

  const previewHeader = useMemo(() => {
    let text = form.headerText;
    const params = extractParams(text);
    params.forEach((p, i) => {
      const example = headerExamples[i]?.trim();
      if (example) {
        text = text.replace(p, example);
      }
    });
    return text;
  }, [form.headerText, headerExamples]);

  return (
    <div className="wa-tpl-layout">
      <form onSubmit={handleSubmit} className="wa-tpl-form">
        {error && (
          <div className="wa-tpl-error">
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {/* ── Section: Template name and language ─────────────────────── */}
        <div className="wa-tpl-section">
          <h6 className="wa-tpl-section-title">Template name and language</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="wa-tpl-label">Name your template</Form.Label>
                <div className="wa-tpl-input-counter">
                  <Form.Control
                    type="text"
                    placeholder="e.g. welcome_message"
                    value={form.name}
                    onChange={update("name")}
                    disabled={submitting}
                    maxLength={512}
                  />
                  <span className="wa-tpl-counter">{form.name.length}/512</span>
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="wa-tpl-label">Select language</Form.Label>
                <Form.Select value={form.language} onChange={update("language")} disabled={submitting}>
                  {LANGUAGES.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="wa-tpl-label">Category</Form.Label>
                <Form.Select value={form.category} onChange={update("category")} disabled={submitting}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* ── Section: Content ────────────────────────────────────────── */}
        <div className="wa-tpl-section">
          <h6 className="wa-tpl-section-title">Content</h6>
          <p className="wa-tpl-section-desc">
            Add a header, body and footer for your template. Cloud API hosted by Meta will review
            the template variables and content to protect the security and integrity of our services.
          </p>

          {/* Header */}
          <div className="wa-tpl-subsection">
            <Form.Label className="wa-tpl-label">
              Header <span className="text-muted fw-normal">· Optional</span>
            </Form.Label>
            <Row className="mb-2">
              <Col md={3}>
                <Form.Select value={form.headerType} onChange={update("headerType")} disabled={submitting}>
                  {HEADER_TYPES.map((h) => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            {form.headerType === "text" && (
              <>
                <div className="wa-tpl-input-counter mb-1">
                  <Form.Control
                    type="text"
                    placeholder="Add a short line of text to the header of your message"
                    value={form.headerText}
                    onChange={update("headerText")}
                    maxLength={60}
                    disabled={submitting}
                  />
                  <span className="wa-tpl-counter">{form.headerText.length}/60</span>
                </div>
                <div className="wa-tpl-add-var-row">
                  {headerParamCount < 1 && (
                    <button type="button" className="wa-tpl-add-var-btn" onClick={addHeaderVariable} disabled={submitting}>
                      + Add variable
                    </button>
                  )}
                </div>
                {/* Header variable samples */}
                {headerParamCount > 0 && (
                  <div className="wa-tpl-variable-samples">
                    <div className="wa-tpl-variable-samples-header">
                      <span className="wa-tpl-variable-samples-title">Variable samples</span>
                    </div>
                    <p className="wa-tpl-variable-samples-desc">
                      Include samples of all variables in your message to help Meta review your template.
                    </p>
                    <div className="wa-tpl-label mb-1">Header</div>
                    {Array.from({ length: headerParamCount }, (_, i) => (
                      <div key={`hdr-${i}`} className="wa-tpl-sample-row">
                        <span className="wa-tpl-sample-label">{`{{${i + 1}}}`}</span>
                        <Form.Control
                          type="text"
                          placeholder={`Enter content for {{${i + 1}}}`}
                          value={headerExamples[i] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setHeaderExamples((prev) => {
                              const next = [...prev];
                              next[i] = val;
                              return next;
                            });
                          }}
                          disabled={submitting}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Body */}
          <div className="wa-tpl-subsection">
            <Form.Label className="wa-tpl-label">Body <span className="text-danger">*</span></Form.Label>
            <div className="wa-tpl-textarea-wrap">
              <Form.Control
                as="textarea"
                rows={5}
                placeholder={"Hello {{1}},\n\nYour class starts at {{2}}.\n\nThank you!"}
                value={form.body}
                onChange={update("body")}
                maxLength={1024}
                disabled={submitting}
              />
              <div className="wa-tpl-textarea-footer">
                <button type="button" className="wa-tpl-add-var-btn" onClick={addBodyVariable} disabled={submitting}>
                  + Add variable
                </button>
                <span className="wa-tpl-counter">{bodyCharCount}/1024</span>
              </div>
            </div>
          </div>

          {/* Body variable samples */}
          {bodyParamCount > 0 && (
            <div className="wa-tpl-variable-samples">
              <div className="wa-tpl-variable-samples-header">
                <span className="wa-tpl-variable-samples-title">Variable samples</span>
              </div>
              <p className="wa-tpl-variable-samples-desc">
                Include samples of all variables in your message to help Meta review your template.
                Remember not to include any customer information to protect your customer's privacy.
              </p>
              <div className="wa-tpl-label mb-1">Body</div>
              {Array.from({ length: bodyParamCount }, (_, i) => (
                <div key={`body-${i}`} className="wa-tpl-sample-row">
                  <span className="wa-tpl-sample-label">{`{{${i + 1}}}`}</span>
                  <Form.Control
                    type="text"
                    placeholder={`Enter content for {{${i + 1}}}`}
                    value={bodyExamples[i] || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBodyExamples((prev) => {
                        const next = [...prev];
                        next[i] = val;
                        return next;
                      });
                    }}
                    disabled={submitting}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="wa-tpl-subsection">
            <Form.Label className="wa-tpl-label">
              Footer <span className="text-muted fw-normal">· Optional</span>
            </Form.Label>
            <div className="wa-tpl-input-counter">
              <Form.Control
                type="text"
                placeholder="Add a short line of text to the bottom of your message"
                value={form.footer}
                onChange={update("footer")}
                maxLength={60}
                disabled={submitting}
              />
              <span className="wa-tpl-counter">{form.footer.length}/60</span>
            </div>
          </div>
        </div>

        {/* ── Section: Buttons ────────────────────────────────────────── */}
        <div className="wa-tpl-section">
          <h6 className="wa-tpl-section-title">
            Buttons <span className="text-muted fw-normal">· Optional</span>
          </h6>
          <p className="wa-tpl-section-desc">
            Create buttons that let customers respond to your message or take action.
            If you add more than three buttons, they will appear in a list.
          </p>

          {/* Existing buttons */}
          {form.buttons.map((btn, idx) => (
            <div key={`btn-${idx}`} className="wa-tpl-button-card">
              <div className="wa-tpl-button-card-header">
                <span className="wa-tpl-button-type-label">
                  {btn.type === "URL" && "Visit website"}
                  {btn.type === "PHONE_NUMBER" && "Call Phone Number"}
                  {btn.type === "QUICK_REPLY" && "Custom"}
                  {btn.type === "COPY_CODE" && "Copy offer code"}
                </span>
                <button type="button" className="wa-tpl-button-remove" onClick={() => removeButton(idx)}>
                  <X size={16} />
                </button>
              </div>
              <div className="wa-tpl-button-card-body">
                {btn.type === "QUICK_REPLY" && (
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="wa-tpl-label-sm">Button text</Form.Label>
                        <div className="wa-tpl-input-counter">
                          <Form.Control
                            type="text"
                            placeholder="Button text"
                            value={btn.text}
                            onChange={(e) => updateButton(idx, "text", e.target.value)}
                            maxLength={25}
                            disabled={submitting}
                          />
                          <span className="wa-tpl-counter">{(btn.text || "").length}/25</span>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {btn.type === "URL" && (
                  <Row className="align-items-end">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="wa-tpl-label-sm">Button text</Form.Label>
                        <div className="wa-tpl-input-counter">
                          <Form.Control
                            type="text"
                            placeholder="Visit website"
                            value={btn.text}
                            onChange={(e) => updateButton(idx, "text", e.target.value)}
                            maxLength={25}
                            disabled={submitting}
                          />
                          <span className="wa-tpl-counter">{(btn.text || "").length}/25</span>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="wa-tpl-label-sm">URL type</Form.Label>
                        <Form.Select
                          value={btn.urlType || "static"}
                          onChange={(e) => updateButton(idx, "urlType", e.target.value)}
                          disabled={submitting}
                        >
                          <option value="static">Static</option>
                          <option value="dynamic">Dynamic</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="wa-tpl-label-sm">Website URL</Form.Label>
                        <div className="wa-tpl-input-counter">
                          <Form.Control
                            type="text"
                            placeholder="https://www.example.com"
                            value={btn.url}
                            onChange={(e) => updateButton(idx, "url", e.target.value)}
                            maxLength={2000}
                            disabled={submitting}
                          />
                          <span className="wa-tpl-counter">{(btn.url || "").length}/2000</span>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {btn.type === "PHONE_NUMBER" && (
                  <Row>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label className="wa-tpl-label-sm">Button text</Form.Label>
                        <div className="wa-tpl-input-counter">
                          <Form.Control
                            type="text"
                            placeholder="Call us"
                            value={btn.text}
                            onChange={(e) => updateButton(idx, "text", e.target.value)}
                            maxLength={25}
                            disabled={submitting}
                          />
                          <span className="wa-tpl-counter">{(btn.text || "").length}/25</span>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label className="wa-tpl-label-sm">Phone number</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="+91 9876543210"
                          value={btn.phone_number}
                          onChange={(e) => updateButton(idx, "phone_number", e.target.value)}
                          disabled={submitting}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {btn.type === "COPY_CODE" && (
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="wa-tpl-label-sm">Offer code example</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. DISCOUNT10"
                          value={btn.example || ""}
                          onChange={(e) => updateButton(idx, "example", e.target.value)}
                          disabled={submitting}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          ))}

          {/* Add button menu */}
          {form.buttons.length < 10 && (
            <div className="wa-tpl-add-btn-wrap">
              <AppButton type="button" variant="outline" size="sm" onClick={() => setShowBtnMenu(!showBtnMenu)}>
                <Plus size={14} /> Add button <ChevronDown size={14} />
              </AppButton>
              {showBtnMenu && (
                <div className="wa-tpl-btn-menu">
                  {BUTTON_TYPES.map((bt) => {
                    const Icon = bt.icon;
                    return (
                      <button
                        key={bt.value}
                        type="button"
                        className="wa-tpl-btn-menu-item"
                        onClick={() => { addButton(bt.value); setShowBtnMenu(false); }}
                      >
                        <Icon size={14} />
                        <div>
                          <div className="wa-tpl-btn-menu-label">{bt.label}</div>
                          <div className="wa-tpl-btn-menu-desc">{bt.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div className="wa-tpl-actions">
          {onCancel && (
            <AppButton type="button" variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
              Discard
            </AppButton>
          )}
          <AppButton type="submit" size="sm" disabled={submitting}>
            {submitting ? <><Spinner animation="border" size="sm" /> Submitting...</> : submitLabel}
          </AppButton>
        </div>
      </form>

      {/* ── Template Preview (right side) ─────────────────────────────── */}
      <div className="wa-tpl-preview-panel">
        <h6 className="wa-tpl-preview-title">Template preview</h6>
        <div className="wa-tpl-preview-phone">
          <div className="wa-tpl-preview-bubble">
            {form.headerType === "text" && form.headerText.trim() && (
              <div className="wa-tpl-preview-header">{previewHeader}</div>
            )}
            {form.body.trim() ? (
              <div className="wa-tpl-preview-body">{previewBody}</div>
            ) : (
              <div className="wa-tpl-preview-body wa-tpl-preview-empty">No body content yet</div>
            )}
            {form.footer.trim() && (
              <div className="wa-tpl-preview-footer">{form.footer}</div>
            )}
            <div className="wa-tpl-preview-time">
              {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          {form.buttons.length > 0 && (
            <div className="wa-tpl-preview-buttons">
              {form.buttons.slice(0, 3).map((b, i) => (
                <div key={`pbtn-${i}`} className="wa-tpl-preview-btn">
                  {b.type === "URL" && <Globe size={13} className="me-1" />}
                  {b.type === "PHONE_NUMBER" && <Phone size={13} className="me-1" />}
                  {b.type === "COPY_CODE" && <Copy size={13} className="me-1" />}
                  {b.text || (b.type === "COPY_CODE" ? "Copy offer code" : `Button ${i + 1}`)}
                </div>
              ))}
              {form.buttons.length > 3 && (
                <div className="wa-tpl-preview-btn wa-tpl-preview-btn-more">
                  See all options
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateForm;
